import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  AlertTriangle, Clock, CheckCircle2, RefreshCw, Loader2, Mail,
  Activity, ShieldAlert, Building2, Users, Timer,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OUTCOME_PILL = {
  breached: 'bg-red-50 text-red-700 border-red-200',
  at_risk: 'bg-amber-50 text-amber-700 border-amber-200',
  on_track: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  met: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const ADMIN_NAV = [
  { icon: Activity, label: 'Operations Executive', href: '/operations/executive' },
  { icon: Building2, label: 'Referral Sources', href: '/operations/referral-sources' },
  { icon: Timer, label: 'Referral SLA', active: true },
  { icon: ShieldAlert, label: 'EVV Compliance', href: '/admin/evv' },
  { icon: Users, label: 'Clinician Retention', href: '/admin/clinician-retention' },
];

function fmt(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(); } catch { return '—'; }
}
function hours(h) {
  if (h === null || h === undefined) return '—';
  const sign = h < 0 ? '−' : '';
  const abs = Math.abs(h);
  if (abs < 1) return `${sign}${Math.round(abs * 60)}m`;
  return `${sign}${abs.toFixed(1)}h`;
}

export default function AdminReferralSla() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [hist, setHist] = useState(null);
  const [windowDays, setWindowDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [briefingSending, setBriefingSending] = useState(false);
  const [briefingPayload, setBriefingPayload] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [live, history] = await Promise.all([
        axios.get(`${API}/operations/referral-sla`, getAuthHeader()),
        axios.get(`${API}/operations/referral-sla/history`, { ...getAuthHeader(), params: { window_days: windowDays } }),
      ]);
      setData(live.data);
      setHist(history.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load referral SLA dashboard');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, windowDays]);

  useEffect(() => { load(); }, [load]);

  const previewBriefing = useCallback(async () => {
    setBriefingSending(true);
    try {
      const res = await axios.get(`${API}/admin/operations/briefing/preview`, getAuthHeader());
      setBriefingPayload(res.data.payload);
      toast.success('Briefing payload refreshed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate briefing preview');
    } finally {
      setBriefingSending(false);
    }
  }, [getAuthHeader]);

  const sendBriefingNow = useCallback(async () => {
    setBriefingSending(true);
    try {
      const res = await axios.post(`${API}/admin/operations/briefing/send`, {}, getAuthHeader());
      const r = res.data?.result || {};
      toast.success(`Briefing sent to ${r.sent || 0} admin(s). ${r.errors ? `${r.errors} delivery error(s).` : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send briefing');
    } finally {
      setBriefingSending(false);
    }
  }, [getAuthHeader]);

  const kpis = data?.kpis || {};
  const pending = data?.pending || [];
  const closed = data?.closed || [];

  const breachedFirst = useMemo(() => pending.filter((p) => p.sla_outcome === 'breached').slice(0, 8), [pending]);

  return (
    <DashboardShell
      navItems={ADMIN_NAV}
      title="Referral SLA Tracker"
      subtitle="Time-to-acceptance for every inbound referral. Intervene BEFORE the referring hospital cancels and routes elsewhere."
      testId="admin-referral-sla-page"
    >
      {/* CONTROLS */}
      <div className="cn-card p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="sla-controls">
        <div className="flex items-center gap-2">
          <span className="text-xs text-cn-text-muted font-semibold">History window</span>
          <select
            value={windowDays}
            onChange={(e) => setWindowDays(Number(e.target.value))}
            className="border border-cn-border rounded-lg px-2 py-1.5 text-sm bg-white"
            data-testid="sla-window-select"
          >
            {[7, 14, 30, 60, 90, 180, 365].map((d) => <option key={d} value={d}>Last {d} days</option>)}
          </select>
        </div>
        <Button onClick={load} disabled={loading} className="cn-btn cn-btn-ghost !py-2" data-testid="sla-refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
        <div className="flex-1" />
        <Button onClick={previewBriefing} disabled={briefingSending} className="cn-btn cn-btn-ghost !py-2" data-testid="briefing-preview-btn">
          Preview weekly briefing
        </Button>
        <Button onClick={sendBriefingNow} disabled={briefingSending} className="cn-btn cn-btn-primary !py-2" data-testid="briefing-send-btn">
          <Mail className="w-4 h-4" /> Send briefing now
        </Button>
      </div>

      {loading && <div className="cn-card p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-cn-primary mx-auto" /></div>}

      {!loading && data && (
        <>
          {/* KPI TILES */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="sla-kpis">
            <KpiTile label="Open / pending" value={kpis.open_pending || 0} icon={Clock} testid="sla-kpi-open" />
            <KpiTile
              label="In-flight breaches"
              value={kpis.in_flight_breaches || 0}
              icon={AlertTriangle}
              tone="red"
              sub="Passed deadline · not yet accepted"
              testid="sla-kpi-breaches"
            />
            <KpiTile
              label="At-risk pending"
              value={kpis.at_risk_count || 0}
              icon={Timer}
              tone="amber"
              sub="≥ 75% of SLA elapsed"
              testid="sla-kpi-atrisk"
            />
            <KpiTile
              label="24h hit-rate"
              value={`${(kpis.met_pct_24h || 0).toFixed(1)}%`}
              icon={CheckCircle2}
              tone="emerald"
              sub={`${kpis.closed_24h || 0} closed in last 24h`}
              testid="sla-kpi-hitrate"
            />
          </div>

          {/* BREACHED CALLOUT */}
          {breachedFirst.length > 0 && (
            <div className="cn-card p-5 mb-6 bg-red-50/40 border-red-200" data-testid="sla-breach-callout">
              <h4 className="font-display font-semibold text-base text-cn-text mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                Top in-flight breaches ({breachedFirst.length})
              </h4>
              <div className="grid md:grid-cols-2 gap-3">
                {breachedFirst.map((r) => (
                  <div key={r.referral_id} className="cn-card p-3 bg-white" data-testid={`sla-breach-${r.referral_id}`}>
                    <div className="flex items-center justify-between">
                      <p className="font-semibold text-cn-text text-sm">{r.organization}</p>
                      <span className="text-[10px] px-2 py-0.5 rounded-full border font-semibold bg-red-50 text-red-700 border-red-200">{r.urgency}</span>
                    </div>
                    <p className="text-xs text-cn-text-muted">Care type: {r.care_type || '—'} · State: {r.state || '—'}</p>
                    <p className="text-xs text-red-700 mt-1">Over by {hours(r.time_remaining_hours)} (SLA {r.sla_hours}h)</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* PENDING TABLE */}
          <div className="cn-card overflow-hidden mb-6" data-testid="sla-pending-card">
            <div className="p-5 border-b border-cn-border">
              <h4 className="font-display font-semibold text-base text-cn-text">Pending referrals ({pending.length})</h4>
              <p className="text-xs text-cn-text-muted mt-0.5">Sorted: breached → at-risk → on-track. Oldest first within each group.</p>
            </div>
            {pending.length === 0 ? (
              <div className="p-10 text-center" data-testid="sla-pending-empty">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                <p className="text-sm text-cn-text-muted">Inbox zero — every open referral is on-track.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="sla-pending-table">
                  <thead className="bg-cn-bone-50 border-b border-cn-border text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">
                    <tr className="text-left">
                      <th className="px-5 py-3">Organization</th>
                      <th className="px-3 py-3">Urgency</th>
                      <th className="px-3 py-3">Care type</th>
                      <th className="px-3 py-3">State</th>
                      <th className="px-3 py-3 text-right">Created</th>
                      <th className="px-3 py-3 text-right">Deadline</th>
                      <th className="px-3 py-3 text-right">Time left</th>
                      <th className="px-3 py-3">SLA outcome</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-cn-border">
                    {pending.slice(0, 50).map((r) => (
                      <tr key={r.referral_id} className="hover:bg-cn-bone-50/40" data-testid={`sla-row-${r.referral_id}`}>
                        <td className="px-5 py-2.5 font-medium">{r.organization}</td>
                        <td className="px-3 py-2.5 text-cn-text-muted text-xs">{r.urgency}</td>
                        <td className="px-3 py-2.5 text-cn-text-muted text-xs">{r.care_type || '—'}</td>
                        <td className="px-3 py-2.5 text-cn-text-muted text-xs">{r.state || '—'}</td>
                        <td className="px-3 py-2.5 text-right text-cn-text-muted text-xs">{fmt(r.created_at)}</td>
                        <td className="px-3 py-2.5 text-right text-cn-text-muted text-xs">{fmt(r.deadline_at)}</td>
                        <td className={`px-3 py-2.5 text-right text-xs font-semibold ${r.time_remaining_hours < 0 ? 'text-red-700' : 'text-cn-text'}`}>{hours(r.time_remaining_hours)}</td>
                        <td className="px-3 py-2.5">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${OUTCOME_PILL[r.sla_outcome]}`}>{r.sla_outcome}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {pending.length > 50 && (
                  <p className="text-xs text-cn-text-muted text-center py-3 border-t border-cn-border">
                    Showing top 50 of {pending.length}. Refine pipeline upstream to clear the queue.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* HISTORY */}
          {hist && (
            <div className="cn-card p-5 mb-6" data-testid="sla-history-card">
              <h4 className="font-display font-semibold text-base text-cn-text mb-3">Historic SLA performance · Last {hist.window_days} days</h4>
              <div className="grid sm:grid-cols-3 gap-3 mb-4">
                <HistTile label="Total closed" value={hist.overall.total} />
                <HistTile label="Met" value={hist.overall.met} tone="emerald" />
                <HistTile label="Breached" value={hist.overall.breached} tone="red" />
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-2">By urgency</p>
                  <div className="space-y-1.5">
                    {Object.entries(hist.by_urgency || {}).map(([u, v]) => (
                      <div key={u} className="flex items-center justify-between text-xs">
                        <span className="text-cn-text capitalize">{u}</span>
                        <span className={`font-semibold ${v.met_pct >= 80 ? 'text-emerald-700' : v.met_pct >= 50 ? 'text-amber-700' : 'text-red-700'}`}>{v.met_pct.toFixed(1)}% met</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-2">Lowest hit-rates by source</p>
                  <div className="space-y-1.5">
                    {(hist.by_source || []).slice(0, 6).map((s) => (
                      <div key={s.organization} className="flex items-center justify-between text-xs">
                        <span className="text-cn-text truncate">{s.organization}</span>
                        <span className={`font-semibold ml-3 ${s.met_pct >= 80 ? 'text-emerald-700' : s.met_pct >= 50 ? 'text-amber-700' : 'text-red-700'}`}>{s.met_pct.toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* BRIEFING PREVIEW (collapsible result of preview action) */}
          {briefingPayload && (
            <div className="cn-card p-5 mb-6" data-testid="briefing-preview-card">
              <h4 className="font-display font-semibold text-base text-cn-text mb-3">Weekly briefing preview</h4>
              <pre className="text-xs bg-cn-bone-50 border border-cn-border rounded-lg p-3 overflow-auto max-h-72">
                {JSON.stringify(briefingPayload, null, 2)}
              </pre>
            </div>
          )}

          {/* CLOSED IN LAST 72H */}
          {closed.length > 0 && (
            <div className="cn-card overflow-hidden" data-testid="sla-closed-card">
              <div className="p-5 border-b border-cn-border">
                <h4 className="font-display font-semibold text-base text-cn-text">Recently closed ({closed.length})</h4>
                <p className="text-xs text-cn-text-muted mt-0.5">Last 72 hours · acceptance outcomes</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="sla-closed-table">
                  <thead className="bg-cn-bone-50 border-b border-cn-border text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">
                    <tr className="text-left">
                      <th className="px-5 py-3">Organization</th>
                      <th className="px-3 py-3">Urgency</th>
                      <th className="px-3 py-3 text-right">Accepted at</th>
                      <th className="px-3 py-3 text-right">Time to accept</th>
                      <th className="px-3 py-3">Outcome</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-cn-border">
                    {closed.slice(0, 30).map((r) => (
                      <tr key={r.referral_id} className="hover:bg-cn-bone-50/40" data-testid={`sla-closed-row-${r.referral_id}`}>
                        <td className="px-5 py-2.5 font-medium">{r.organization}</td>
                        <td className="px-3 py-2.5 text-cn-text-muted text-xs">{r.urgency}</td>
                        <td className="px-3 py-2.5 text-right text-cn-text-muted text-xs">{fmt(r.accepted_at)}</td>
                        <td className="px-3 py-2.5 text-right text-cn-text">{hours(r.time_to_accept_hours)}</td>
                        <td className="px-3 py-2.5">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${OUTCOME_PILL[r.sla_outcome]}`}>{r.sla_outcome}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </DashboardShell>
  );
}

function KpiTile({ label, value, sub, icon: Icon, tone = 'neutral', testid }) {
  const iconCls = {
    neutral: 'text-cn-primary',
    red: 'text-red-600',
    amber: 'text-amber-600',
    emerald: 'text-emerald-600',
  }[tone];
  return (
    <div className="cn-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">{label}</span>
        <Icon className={`w-4 h-4 ${iconCls}`} />
      </div>
      <p className="font-display font-bold text-3xl text-cn-text dark:text-white">{value}</p>
      {sub && <p className="text-xs text-cn-text-muted mt-1">{sub}</p>}
    </div>
  );
}

function HistTile({ label, value, tone = 'neutral' }) {
  const cls = {
    neutral: 'text-cn-text',
    red: 'text-red-700',
    emerald: 'text-emerald-700',
  }[tone];
  return (
    <div className="cn-card p-3 bg-white">
      <p className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">{label}</p>
      <p className={`font-display font-bold text-2xl ${cls}`}>{value}</p>
    </div>
  );
}
